import jsQR from 'jsqr';
import { renderQrCanvas } from './qrCodeEngine';

/**
 * Scannability checks. Everything here is about whether a *phone* will read the
 * code, not about whether it looks good — the two pull in opposite directions
 * often enough to deserve their own panel.
 *
 * Two layers: design heuristics (contrast, logo, quiet zone…) and a real decode
 * of the rendered artwork. The decode is the one that settles arguments — a
 * design can look fine by every rule and still fail to read.
 */

/** Relative luminance (WCAG), used for the contrast ratio. */
function luminance(hex) {
    let value = String(hex || '#000000').replace('#', '');
    if (value.length === 3) value = value.split('').map((c) => c + c).join('');
    const channel = (offset) => {
        const raw = parseInt(value.substr(offset, 2), 16) / 255;
        return raw <= 0.03928 ? raw / 12.92 : ((raw + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

export function contrastRatio(fg, bg) {
    const a = luminance(fg);
    const b = luminance(bg);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * Decode the artwork the user is actually looking at.
 *
 * Rendered without the outer frame (it sits outside the modules and only slows
 * the render) but with everything that can hurt a read: logo, background image,
 * pattern, colours, quiet zone.
 *
 * `scale` is a downsample factor — reading at a smaller size approximates a
 * phone that is far away or a low-resolution print.
 *
 * @returns {Promise<{status:'readable'|'mismatch'|'unreadable'|'error', decoded?:string}>}
 */
export async function decodeQrArtwork(payload, design, size = 560) {
    if (!payload) return { status: 'error' };
    try {
        const canvas = await renderQrCanvas(payload, { ...design, frameType: 'none' }, size);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const result = jsQR(image.data, canvas.width, canvas.height);
        if (!result) return { status: 'unreadable' };
        if (result.data !== payload) return { status: 'mismatch', decoded: result.data };
        return { status: 'readable', decoded: result.data };
    } catch {
        return { status: 'error' };
    }
}

/**
 * Score the current design out of 100 and list what is hurting it.
 *
 * @param {object|null} verification result of `decodeQrArtwork`, when it has run
 * @returns {{score:number, contrast:number, issues:{level:'ok'|'warn'|'bad', key:string}[], verified:boolean}}
 */
export function auditQrDesign(design, payload, verification) {
    const bg = design.transparentBg ? '#ffffff' : design.bgColor;
    const fg = design.gradientEnabled ? design.gradientFrom : design.fgColor;
    const contrast = contrastRatio(fg, bg);
    const issues = [];
    let score = 100;

    const flag = (level, key, penalty) => { issues.push({ level, key }); score -= penalty; };

    if (contrast < 3) flag('bad', 'contrastLow', 45);
    else if (contrast < 5) flag('warn', 'contrastFair', 18);

    // An inverted code (light modules on a dark field) defeats many readers.
    if (luminance(fg) > luminance(bg)) flag('warn', 'inverted', 20);

    if (design.logoDataUrl && design.logoSizePercent > 25 && design.errorLevel !== 'H') {
        flag('bad', 'logoEcc', 25);
    }
    if ((design.quietZonePercent ?? 4) < 2) flag('warn', 'quietZone', 15);
    if (design.bgImageDataUrl && (design.bgImageOpacity ?? 20) > 35) flag('warn', 'bgImage', 15);
    if ((payload || '').length > 400 && design.errorLevel === 'H') flag('warn', 'dense', 10);
    if ((design.cornerRadiusPercent || 0) > 12) flag('warn', 'cornerRadius', 8);

    // A failed decode outranks every heuristic: the code demonstrably does not
    // read, so the score is capped no matter how clean the design looks.
    let verified = false;
    if (verification?.status === 'unreadable') {
        issues.unshift({ level: 'bad', key: 'decodeFailed' });
        score = Math.min(score, 30);
    } else if (verification?.status === 'mismatch') {
        issues.unshift({ level: 'bad', key: 'decodeMismatch' });
        score = Math.min(score, 40);
    } else if (verification?.status === 'readable') {
        verified = true;
    }

    score = Math.max(0, Math.min(100, Math.round(score)));
    // The card already carries a "verified by a reader" banner, so a clean audit
    // just says the design itself is fine.
    if (!issues.length) issues.push({ level: 'ok', key: 'allGood' });

    return { score, contrast, issues, verified };
}

export function auditGrade(score) {
    if (score >= 85) return 'great';
    if (score >= 65) return 'okay';
    return 'risky';
}
